import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { ServiceVersionsDocument } from "#app/lib/data";
import { APP_VERSION } from "#app/lib/app-version";

export interface ServiceVersionInfo {
  id: string;
  /** Resolved version, `"unreachable"` on a failed probe, null while loading. */
  version: string | null;
}

/**
 * Per-service versions for the About panel. Core probes every backend service
 * server-side (`serviceVersions` query) so the browser never issues
 * cross-origin `/version` fetches that CORS turns into false "unreachable" —
 * and the service roster lives in exactly one place (core's
 * `SystemHealthService.targets()`). The client's own build version is the
 * build-time constant, listed first.
 *
 * Fetches when `enabled` flips true (dialog open); the report is built at copy
 * time by the caller so its timestamp reflects the copy, not the load.
 */
export function useServiceVersions(enabled: boolean): {
  versions: ServiceVersionInfo[];
} {
  const { data } = useQuery(ServiceVersionsDocument, {
    skip: !enabled,
    fetchPolicy: "cache-and-network",
  });

  const versions = useMemo<ServiceVersionInfo[]>(() => {
    const app: ServiceVersionInfo = { id: "app", version: APP_VERSION };
    // Services fill in once the query resolves; the app's build version shows
    // immediately.
    const services = (data?.serviceVersions ?? []).map((s) => ({
      id: s.service,
      version: s.version,
    }));
    return [app, ...services];
  }, [data]);

  return { versions };
}

/** A paste-ready support block, built at call time so its timestamp is now. */
export function formatVersionReport(versions: ServiceVersionInfo[]): string {
  return [
    `SalesTrak version report — ${new Date().toISOString()}`,
    ...versions.map(({ id, version }) => `${id}: ${version ?? "…"}`),
  ].join("\n");
}
